import { Link, useForm } from "@inertiajs/react";
import { new_bizminer_app_password_reset_path } from "@/_generated/routes/routes";
import type BizminerOrganizationDisplay from "@/_generated/types/serializers/BizminerOrganizationDisplay";
import { AuthCard } from "@/components/auth/AuthCard.tsx";
import { SsoButton, type SsoConfig } from "@/components/auth/SsoButton.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";

export type { SsoConfig };

interface DividerProps {
  text: string;
}

const Divider = ({ text }: DividerProps) => (
  <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
    <span className="bg-card text-muted-foreground relative z-10 px-2">
      {text}
    </span>
  </div>
);

interface EmailPasswordFormProps {
  email: string;
  password: string;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  processing: boolean;
  errors: { email_address?: string; password?: string };
  forgotPasswordPath: string;
  showDivider?: boolean;
}

const EmailPasswordForm = ({
  email,
  password,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  processing,
  errors,
  forgotPasswordPath,
  showDivider,
}: EmailPasswordFormProps) => (
  <form onSubmit={onSubmit}>
    <div className="flex flex-col gap-4">
      {showDivider && <Divider text="Or with email" />}

      <div className="grid gap-2">
        <Label htmlFor="email_address">Email address</Label>
        <Input
          id="email_address"
          name="email_address"
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          required
        />
        {errors.email_address && (
          <p className="text-sm text-destructive">{errors.email_address}</p>
        )}
      </div>

      <div className="grid gap-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          placeholder="Password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          required
        />
        {errors.password && (
          <p className="text-sm text-destructive">{errors.password}</p>
        )}
      </div>

      <div className="flex justify-end">
        <Link
          href={forgotPasswordPath}
          className="text-sm text-muted-foreground underline underline-offset-4 hover:text-primary"
        >
          Forgot password?
        </Link>
      </div>

      <Button type="submit" className="w-full mt-2" disabled={processing}>
        {processing ? "Signing in..." : "Sign in"}
      </Button>
    </div>
  </form>
);

interface DevLoginButtonProps {
  onClick: () => void;
  processing: boolean;
}

const DevLoginButton = ({ onClick, processing }: DevLoginButtonProps) => (
  <>
    <Divider text="Dev only" />
    <Button
      type="button"
      variant="destructive"
      onClick={onClick}
      disabled={processing}
      className="w-full"
    >
      Auto Login (Dev)
    </Button>
  </>
);

interface Props {
  session_url: string;
  organization_slug: string;
  dangerous_magic_url_never_on_production?: string;
  sso?: SsoConfig;
  organization?: BizminerOrganizationDisplay;
}

export function LoginForm({
  session_url,
  organization_slug,
  dangerous_magic_url_never_on_production,
  sso,
  organization,
}: Props) {
  const { data, setData, post, processing, errors } = useForm({
    email_address: "",
    password: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post(session_url);
  };

  const handleAutoLogin = () => {
    if (dangerous_magic_url_never_on_production) {
      window.location.href = dangerous_magic_url_never_on_production;
    }
  };

  const showDevLogin =
    (import.meta.env.DEV || import.meta.env.RAILWAY_PR_ENVIRONMENT) &&
    dangerous_magic_url_never_on_production;

  const ssoOnly = sso?.enabled;

  return (
    <AuthCard organization={organization} subtitle="Sign in to your workspace">
      {sso?.enabled && <SsoButton sso={sso} />}

      {!ssoOnly && (
        <EmailPasswordForm
          email={data.email_address}
          password={data.password}
          onEmailChange={(value) => setData("email_address", value)}
          onPasswordChange={(value) => setData("password", value)}
          onSubmit={handleSubmit}
          processing={processing}
          errors={errors}
          forgotPasswordPath={new_bizminer_app_password_reset_path(
            organization_slug,
          )}
          showDivider={sso?.enabled}
        />
      )}

      {showDevLogin && (
        <DevLoginButton onClick={handleAutoLogin} processing={processing} />
      )}
    </AuthCard>
  );
}
